/**
 * Vision 2040 Translations Module (English)
 * 
 * Contains: Vision 2040 Strategic Plan, Milestones, Scenario Simulator, Strategic Pillars
 * 
 * @module vision2040
 * @language en
 */

export const vision2040 = {
  vision2040Hero: {
    membersTarget: "Members Target",
    countryTarget: "Country Target",
    annualBeneficiaries: "Annual Beneficiaries"
  },
  vision2040TechTargets: {
    aiUsers: "400K+ users",
    vrTrainers: "10K+ trainers",
    appDownloads: "300K+ downloads",
    sustainabilityCountries: "80 countries"
  },
  // Root level strategicPillarsData - components use t('strategicPillarsData.xxx')
  strategicPillarsData: {
    // Leadership Excellence Pillar
    expandTraining: "Expand Leadership Training Programs",
    certifiedTrainers: "10,000 Certified Trainers",
    goldStandard: "Gold Standard Curriculum",
    trainingTarget: "100K trained annually by 2040",
    expandDetail1: "Scale JCI Academy to 130+ countries",
    expandDetail2: "Develop 10K certified trainers globally",
    expandDetail3: "Launch multi-language training platform",
    expandDetail4: "Create industry-specific leadership tracks",
    expandDetail5: "Establish partnerships with universities",
    trainedYear: "Trained/Year",
    
    // Technology Pillar
    aiDashboard: "AI-Powered Dashboard",
    offlineApp: "Offline-First Mobile App",
    impactTracking: "Real-time Impact Tracking",
    techTarget: "80% digital adoption by 2040",
    techDetail1: "Deploy AI member experience platform",
    techDetail2: "Launch VR leadership simulation program",
    techDetail3: "Create unified global data dashboard",
    techDetail4: "Implement blockchain impact verification",
    techDetail5: "Build accessible mobile-first platform",
    users: "Users",
    
    // Sustainability Pillar
    climateAction: "Climate Action Integration",
    carbonNeutral: "Carbon Neutral Operations",
    renewableEnergy: "100% Renewable Energy",
    sustainabilityTarget: "Net-zero by 2030",
    sustainDetail1: "Achieve carbon neutral operations by 2030",
    sustainDetail2: "Integrate SDG mapping for all projects",
    sustainDetail3: "Create green certification program",
    sustainDetail4: "Partner with environmental organizations",
    sustainDetail5: "Develop climate literacy curriculum",
    riseProjects: "RISE Projects",
    
    // Equity & Inclusion Pillar
    genderParity: "Gender Parity Achievement",
    expandUnderrepresented: "Expand to Underrepresented Regions",
    financialAccess: "Financial Access Programs",
    equityTarget: "50% gender parity by 2035",
    equityDetail1: "Achieve 50% gender parity in leadership",
    equityDetail2: "Expand presence in underserved regions",
    equityDetail3: "Create scholarship programs for access",
    equityDetail4: "Develop inclusive leadership frameworks",
    equityDetail5: "Build accessibility infrastructure"
  },
  simulator: {
    factors: {
      conservativeGrowth: "2-3% annual member growth",
      limitedDigital: "Limited digital platform adoption",
      minimalPartnerships: "Minimal new corporate partnerships",
      regionalChallenges: "Regional expansion challenges",
      moderateGrowth: "5-7% annual member growth",
      fullDigital: "Full digital platform deployment",
      strategicPartnerships: "Strategic corporate partnerships",
      regionalExpansion: "Coordinated regional expansion",
      aggressiveGrowth: "10-15% annual member growth",
      aiEngagement: "AI-powered member engagement",
      youthMovement: "Viral youth movement expansion",
      corporateSponsors: "Major corporate sponsors"
    },
    risks: {
      targetsMissed: "Vision 2040 targets may not be achieved",
      reducedInfluence: "Reduced global influence and reach",
      talentGaps: "Talent gaps in underserved regions",
      sustainedInvestment: "Sustained investment required",
      techInfrastructure: "Technology infrastructure required",
      regionalLeadership: "Strong regional leadership needed",
      strainResources: "Resources may be strained",
      qualityQuantity: "Quality vs quantity balance",
      governanceChallenges: "Governance challenges"
    }
  },
  vision2040: {
    title: "JCI Vision 2040",
    pageSubtitle: "Strategic Roadmap to 500,000 Young Leaders",
    transformingFuture: "Transforming the Future of Young Leadership",
    followProgress: "Follow our progress towards a global network of 500,000 young leaders creating sustainable positive impact by 2040.",
    exploreVision: "Explore Vision 2040",
    
    // Tab Labels
    fiiImpact: "YFI Impact",
    timeline: "Timeline",
    simulator: "Simulator",
    globalView: "Global View",
    
    // YFI Impact Section (used by Vision2040FIIImpact component)
    fiiImpactTitle: "YFI Impact by 2040",
    fiiImpactDesc: "Projected Future Investment Index improvements if JCI executes Vision 2040 strategy",
    globalFIIPoints: "Global YFI Points",
    africaMeFII: "Africa/ME YFI Points",
    inequalityReduction: "Inequality Reduction",
    regionalGapClosure: "Regional gap closure",
    highest: "highest",
    mentalHealthPillar: "Mental Health",
    workforceDevelopment: "Workforce Development",
    entrepreneurshipPillar: "Entrepreneurship",
    projectedReach: "Projected Reach",
    fiiImpactLabel: "YFI Impact",
    primaryFocus: "Primary focus",
    regionalFIIProjectionsTitle: "Regional YFI Projections: 2025 vs 2040",
    current2025: "2025 Current",
    projected2040: "2040 Projected",
    maintainLead: "Maintain lead",
    strongGrowth: "Strong growth",
    accelerating: "Accelerating",
    highestImprovement: "Highest improvement",
    sourceFII: "Source: JCI Vision 2040 Strategic Plan | Future Investment Index 2024 Projections",
    
    // Simulator Section (used by Vision2040Simulator component)
    simulatorTitle: "Vision 2040 Scenario Simulator",
    simulatorSubtitle: "Explore three possible futures for JCI. What happens if we exceed expectations—or fall short?",
    baseline2025: "Baseline 2025",
    conservative: "Conservative",
    conservativeDesc: "What if growth stalls? Limited tech adoption, minimal partnerships, economic headwinds.",
    moderate: "Moderate (Target)",
    moderateDesc: "Planned growth trajectory with expected digital adoption and strategic partnerships.",
    aggressive: "Aggressive",
    aggressiveDesc: "Maximum growth with full tech adoption, extensive partnerships, and viral expansion.",
    probability: "Probability",
    scenarioOutcomes: "Scenario Outcomes",
    vsTarget: "vs target",
    onTarget: "On Target",
    keySuccessFactors: "Key Success Factors",
    risksAndChallenges: "Risks & Challenges",
    growthTrajectory: "Growth Trajectory Comparison",
    scenario: "Scenario",
    targetModerate: "Target (Moderate)",
    comparisonSummary: "Scenario Comparison Summary",
    metric: "Metric",
    baseline: "Baseline",
    target2040: "2040 Target",
    sourceSimulator: "Source: JCI Vision 2040 Strategic Plan | Scenarios are illustrative projections",
    
    // Growth Visualization Section
    growthVisualization: "Growth Visualization",
    growthDesc: "Strategic growth trajectory from 2025 baseline to 2040 targets",
    growthVisualizationDesc: "Strategic growth trajectory from 2025 baseline to 2040 targets",
    progress: "Progress",
    members: "Members",
    membersK: "Members (K)",
    targetK: "Target (K)",
    countries: "Countries",
    localOrganizations: "Local Organizations",
    annualProjects: "Annual Projects",
    annualBeneficiaries: "Annual Beneficiaries",
    current: "Current",
    target: "Target",
    growth: "Growth",
    cagr: "CAGR",
    cagrMembers: "~8-10% CAGR",
    nationsGrowth: "+86 nations",
    expansionRegions: "Sub-Saharan Africa, Middle East, Southeast Asia",
    moreGrassrootsHubs: "More grassroots hubs",
    projectOrgAverage: "~1 project/org average",
    youthPopulation: "~1.2% of global youth population",
    
    // Priority levels
    priority: {
      critical: "Critical",
      high: "High",
      medium: "Medium",
      low: "Low"
    },
    
    // Global Presence Section
    globalPresence: "Global JCI Presence",
    globalMembers: "Global Members",
    activeCountries: "Active Countries",
    averageGrowth: "Average Growth",
    regionalDistribution: "Regional Distribution",
    globalPerformance: "Global Performance",
    performanceComparison: "Comparison between current performance vs. Vision 2040 goals",
    detailedAnalysis: "Detailed Regional Analysis",
    featuredCountries: "Featured Countries",
    achievements: "Achievements",
    challenges: "Challenges",
    
    // Main Initiatives Section
    mainInitiatives: "Main Initiatives",
    leadershipDevelopment: "Leadership Development Excellence",
    technologyInnovation: "Technology & Digital Innovation",
    sustainabilityFocus: "Sustainability & Impact Focus",
    globalNetworkGrowth: "Global Network Expansion",
    
    // Strategic Pillars Section
    strategicPillars: "Strategic Pillars",
    strategicPillarsTitle: "4 Strategic Pillars for 2040",
    strategicPillarsSubtitle: "Integrated approach to transform youth opportunity globally",
    strategicPillarsDesc: "Our roadmap is built on four interconnected pillars that together will transform youth opportunity worldwide",
    viewAllInitiatives: "View All Initiatives",
    allInitiatives: "All Initiatives",
    
    pillar1Title: "Mental Health & Wellbeing",
    pillar2Title: "Workforce Development & Skills",
    pillar3Title: "Entrepreneurship & Innovation",
    pillar4Title: "Global Partnership & Scaling",
    
    sustainability: "Sustainability",
    equityInclusion: "Equity & Inclusion",
    current2040Target: "Current → 2040 Target",
    
    // Milestone Roadmap Section
    milestoneRoadmap: "Milestone Roadmap",
    milestoneDesc: "Track our journey from current state to Vision 2040 achievement",
    currentState: "Current State",
    currentStateDesc: "Our starting point - 148,000 members in 114 countries",
    midpointTarget: "Midpoint Target",
    midpointTargetDesc: "Halfway mark - 300,000 members in 150 countries",
    accelerationPhase: "Acceleration Phase",
    accelerationPhaseDesc: "Rapid scaling - 400,000 members in 175 countries",
    visionAchieved: "Vision Achieved",
    visionAchievedDesc: "Full target - 500,000 members in 200 countries",
    inProgress: "In Progress",
    upcoming: "Upcoming",
    completed: "Completed",
    planned: "Planned",
    orgs: "Orgs",
    projects: "Projects",
    beneficiaries: "Beneficiaries",
    keyInitiatives: "Key Initiatives",
    needed: "Needed",
    growthRequired: "growth required",
    sourceStrategicPlan: "Source: JCI Vision 2040 Strategic Plan",
    
    // Timeline Section
    timelineTitle: "Vision 2040 Timeline",
    yearsRemaining: "Years Remaining",
    historicalProgress: "Historical Progress",
    jciFounded: "JCI Founded",
    foundedDesc: "Junior Chamber International established in St. Louis, Missouri",
    globalExpansion: "Global Expansion",
    globalExpansionDesc: "Expanded to 100+ countries worldwide",
    foundationYear: "Foundation Year",
    foundationYearDesc: "Digital transformation and RISE program scaling",
    programs: "Programs",
    implementationProgress: "Implementation Progress",
    
    // Digital Platform Section
    digitalPlatform: "Digital Platform",
    techTarget: "Target: 80%+ of members using digital platform by 2040",
    aiIntegration: "AI Integration",
    aiDesc: "Personalized member experience and program recommendations",
    vrTraining: "VR Leadership Training",
    vrTrainingDesc: "Immersive simulations for leadership development",
    aiDashboard: "AI-Powered Dashboard",
    aiDashboardDesc: "Personalized member experience with AI recommendations",
    
    // Sustainability Goals Section
    sustainabilityGoals: "Sustainability Goals",
    carbonNeutral: "Carbon Neutral Operations",
    carbonNeutralDesc: "100% carbon offset by 2030",
    sdgAlignment: "SDG Alignment",
    sdgDesc: "All programs mapped to UN Sustainable Development Goals",
    
    // Tech Roadmap Section
    techRoadmap: "Technology Roadmap",
    techRoadmapDesc: "Digital transformation milestones on our path to 2040",
    mobileApp: "Mobile Application",
    mobileAppDesc: "Offline-first mobile experience for all members",
    sustainabilityTracking: "Sustainability Tracking",
    sustainabilityTrackingDesc: "Real-time environmental impact monitoring",
    complete: "Complete"
  },
  vision2040Extended: {
    // Tab Labels
    fiiImpactTab: "FII Impact",
    timeline: "Timeline",
    simulator: "Simulator",
    globalView: "Global View",
    
    // Milestone Roadmap Section
    milestoneTitle: "Milestone Roadmap to 2040",
    milestoneSubtitle: "Key targets and progress indicators across four strategic phases",
    milestoneRoadmap: "Milestone Roadmap",
    milestoneRoadmapDesc: "Track our journey from current state to Vision 2040 achievement",
    currentState: "Current State",
    currentStateDesc: "Our starting point - 175,000 members in 115 countries",
    midpointTarget: "Midpoint Target",
    midpointTargetDesc: "Halfway mark - 300,000 members in 130 countries",
    accelerationPhase: "Acceleration Phase",
    accelerationPhaseDesc: "Rapid scaling - 400,000 members in 140 countries",
    visionAchieved: "Vision Achieved",
    visionAchievedDesc: "Full target - 500,000 members in 150 countries",
    inProgress: "In Progress",
    upcoming: "Upcoming",
    completed: "Completed",
    planned: "Planned",
    target: "Target",
    members: "Members",
    countries: "Countries",
    localOrganizations: "Local Organizations",
    orgs: "Orgs",
    projects: "Projects",
    beneficiaries: "Beneficiaries",
    keyInitiatives: "Key Initiatives",
    current: "Current",
    needed: "Needed",
    growthRequired: "growth required",
    sourceStrategicPlan: "Source: JCI Vision 2040 Strategic Plan",
    
    // FII Impact Section
    fiiImpactTitle: "FII Impact by 2040",
    fiiImpactDesc: "Projected Future Investment Index improvements if JCI executes Vision 2040 strategy",
    globalFIIPoints: "Global YFI Points",
    africaMeFII: "Africa/ME YFI Points",
    inequalityReduction: "Inequality Reduction",
    regionalGapClosure: "Regional gap closure",
    highest: "highest",
    mentalHealthPillar: "Mental Health",
    workforceDevelopment: "Workforce Development",
    entrepreneurshipPillar: "Entrepreneurship",
    projectedReach: "Projected Reach",
    fiiImpactLabel: "YFI Impact",
    primaryFocus: "Primary focus",
    regionalFIIProjectionsTitle: "Regional YFI Projections: 2025 vs 2040",
    current2025: "2025 Current",
    projected2040: "2040 Projected",
    maintainLead: "Maintain lead",
    strongGrowth: "Strong growth",
    accelerating: "Accelerating",
    highestImprovement: "Highest improvement",
    sourceFII: "Source: JCI Vision 2040 Strategic Plan | Future Investment Index 2024 Projections",
    
    // Timeline Section
    timelineTitle: "Vision 2040 Timeline",
    timelineDesc: "Historical context and future milestones on our journey to 2040",
    yearsRemaining: "Years Remaining",
    historicalProgress: "Historical Progress",
    jciFounded: "JCI Founded",
    foundedDesc: "Junior Chamber International established",
    globalExpansion: "Global Expansion",
    globalExpansionDesc: "Expanded to 100+ countries worldwide",
    foundationYearDesc: "Digital transformation and RISE program scaling",
    programs: "Programs",
    implementationProgress: "Implementation Progress",
    
    // Simulator Section
    simulatorTitle: "Vision 2040 Scenario Simulator",
    simulatorSubtitle: "Explore three possible futures for JCI. What happens if we exceed expectations—or fall short?",
    baseline2025: "Baseline 2025",
    conservative: "Conservative",
    conservativeDesc: "What if growth stalls? Limited tech adoption, minimal partnerships, economic headwinds.",
    moderate: "Moderate (Target)",
    moderateDesc: "Planned growth trajectory with expected digital adoption and strategic partnerships.",
    aggressive: "Aggressive",
    aggressiveDesc: "Maximum growth with full tech adoption, extensive partnerships, and viral expansion.",
    probability: "Probability",
    scenarioOutcomes: "Scenario Outcomes",
    annualProjects: "Annual Projects",
    vsTarget: "vs target",
    onTarget: "On Target",
    keySuccessFactors: "Key Success Factors",
    risksAndChallenges: "Risks & Challenges",
    growthTrajectory: "Growth Trajectory Comparison",
    scenario: "Scenario",
    targetModerate: "Target (Moderate)",
    comparisonSummary: "Scenario Comparison Summary",
    metric: "Metric",
    baseline: "Baseline",
    target2040: "2040 Target",
    sourceSimulator: "Source: JCI Vision 2040 Strategic Plan | Scenarios are illustrative projections for strategic planning purposes",
    
    // Simulator Factors and Risks
    simulatorData: {
      factors: {
        conservativeGrowth: "2-3% annual member growth",
        limitedDigital: "Limited digital platform adoption",
        minimalPartnerships: "Minimal new corporate partnerships",
        moderateGrowth: "5-7% annual member growth",
        fullDigital: "Full digital platform deployment",
        strategicPartnerships: "Strategic corporate partnerships",
        aggressiveGrowth: "10-15% annual member growth",
        aiEngagement: "AI-powered member engagement",
        youthMovement: "Viral youth movement expansion"
      },
      risks: {
        targetsMissed: "Vision 2040 targets may not be achieved",
        reducedInfluence: "Reduced global influence and reach",
        talentGaps: "Talent gaps in underserved regions",
        moderateRisk: "Moderate execution risk remains",
        resourceConstraints: "Resource constraints possible",
        competitionRisk: "Competition from other organizations",
        executionChallenges: "Significant execution challenges",
        fundingRequirements: "High funding requirements",
        marketChanges: "Market conditions may change"
      }
    },
    
    // Global View Section
    globalPresenceTitle: "JCI Global Presence",
    globalMembers: "Global Members",
    activeCountries: "Active Countries",
    averageGrowth: "Average Growth",
    regionalDistribution: "Regional Distribution",
    globalPerformance: "Global Performance",
    performanceComparison: "Comparison between current performance vs. Vision 2040 goals",
    detailedAnalysis: "Detailed Regional Analysis",
    achievements: "Achievements",
    challenges: "Challenges",
    growth: "Growth",
    priority: {
      critical: "Critical",
      high: "High",
      medium: "Medium",
      low: "Low"
    },
    
    // Milestone Initiatives by Year
    milestoneInitiatives: {
      year2025: {
        initiative1: "Digital Platform Expansion",
        initiative2: "RISE program continuation",
        initiative3: "YFI baseline established",
        initiative4: "Regional growth strategies"
      },
      year2030: {
        initiative1: "Carbon-neutral operations",
        initiative2: "AI-powered member dashboard",
        initiative3: "50% gender parity achieved",
        initiative4: "Digital LMS fully deployed"
      },
      year2035: {
        initiative1: "10,000+ certified trainers",
        initiative2: "VR leadership training deployed",
        initiative3: "Africa/ME YFI gap 50% closed",
        initiative4: "Mobile app 300K+ downloads"
      },
      year2040: {
        initiative1: "500K young leaders network",
        initiative2: "Global YFI improved 4-7 points",
        initiative3: "80%+ digital platform adoption",
        initiative4: "Youth opportunity transformed"
      }
    },
    
    // Pillar Data
    pillarData: {
      mentalHealth: {
        current: "145 RISE projects",
        projection: "5,000+ RISE projects",
        reach: "50M+ reached",
        fiiImpact: "+2-3 points",
        focus: "Africa/ME mental health gap"
      },
      workforce: {
        current: "~50K trained",
        projection: "200K+ youth trained",
        reach: "80% employment rate",
        fiiImpact: "+1-2 points",
        focus: "STEM/digital pathways"
      },
      entrepreneurship: {
        current: "1K entrepreneurs",
        projection: "50K+ supported",
        reach: "$500M+ capital mobilized",
        fiiImpact: "+1-2 points",
        focus: "100K+ trained"
      }
    },
    
    // Region Names
    regionNames: {
      europe: "Europe",
      asiaPacific: "Asia and the Pacific",
      americas: "America",
      africaME: "Africa and the Middle East"
    },
    
    // Radar Chart Subjects
    radarSubjects: {
      leadership: "Leadership",
      technology: "Technology",
      sustainability: "Sustainability",
      partnerships: "Partnerships",
      innovation: "Innovation",
      impact: "Impact"
    },
    
    // Global Regions Data
    globalRegions: {
      americas: {
        name: "America",
        achievements: ["8.0% of global membership", "Strong LO network"],
        challenges: ["Member retention", "Regional diversity"]
      },
      europe: {
        name: "Europe",
        achievements: ["11.1% of global membership", "Sustainability leadership"],
        challenges: ["Aging demographics", "Growth stagnation"]
      },
      asiaPacific: {
        name: "Asia and the Pacific",
        achievements: ["70.7% of global membership", "Highest growth potential"],
        challenges: ["Cultural barriers", "Digital infrastructure"]
      },
      africaME: {
        name: "Africa & Middle East",
        achievements: ["10.3% of membership", "Youth population advantage"],
        challenges: ["Limited resources", "Connectivity gaps"]
      }
    }
  }
} as const;
